import * as THREE from "three";
import { sampleTerrain } from "./planet-terrain";
import type { PlanetDefinition } from "./types";

function spherifiedCube(x: number, y: number, z: number, target = new THREE.Vector3()): THREE.Vector3 {
  const x2 = x * x;
  const y2 = y * y;
  const z2 = z * z;
  target.set(
    x * Math.sqrt(Math.max(0, 1 - y2 * 0.5 - z2 * 0.5 + (y2 * z2) / 3)),
    y * Math.sqrt(Math.max(0, 1 - z2 * 0.5 - x2 * 0.5 + (z2 * x2) / 3)),
    z * Math.sqrt(Math.max(0, 1 - x2 * 0.5 - y2 * 0.5 + (x2 * y2) / 3)),
  );
  return target.normalize();
}

function facePoint(face: number, u: number, v: number, target = new THREE.Vector3()): THREE.Vector3 {
  switch (face) {
    case 0: return spherifiedCube(1, v, -u, target);
    case 1: return spherifiedCube(-1, v, u, target);
    case 2: return spherifiedCube(u, 1, -v, target);
    case 3: return spherifiedCube(u, -1, v, target);
    case 4: return spherifiedCube(u, v, 1, target);
    default: return spherifiedCube(-u, v, -1, target);
  }
}

export interface PlanetVisual {
  group: THREE.Group;
  surface: THREE.Mesh<THREE.BufferGeometry, THREE.MeshStandardMaterial>;
  ocean: THREE.Mesh<THREE.SphereGeometry, THREE.MeshStandardMaterial>;
  atmosphere: THREE.Mesh<THREE.SphereGeometry, THREE.ShaderMaterial>;
}

export function createCubeSpherePlanet(planet: PlanetDefinition, subdivisions = 48): PlanetVisual {
  const radius = planet.diameterMeters * 0.5;
  const positions: number[] = [];
  const normals: number[] = [];
  const colors: number[] = [];
  const indices: number[] = [];
  const direction = new THREE.Vector3();
  const vertexColor = new THREE.Color();

  for (let face = 0; face < 6; face += 1) {
    const base = positions.length / 3;
    for (let y = 0; y <= subdivisions; y += 1) {
      const v = (y / subdivisions) * 2 - 1;
      for (let x = 0; x <= subdivisions; x += 1) {
        const u = (x / subdivisions) * 2 - 1;
        facePoint(face, u, v, direction);
        const sample = sampleTerrain(direction, planet);
        const surfaceRadius = radius + sample.height;
        positions.push(direction.x * surfaceRadius, direction.y * surfaceRadius, direction.z * surfaceRadius);
        normals.push(direction.x, direction.y, direction.z);
        vertexColor.copy(sample.color);
        colors.push(vertexColor.r, vertexColor.g, vertexColor.b);
      }
    }
    const row = subdivisions + 1;
    for (let y = 0; y < subdivisions; y += 1) {
      for (let x = 0; x < subdivisions; x += 1) {
        const a = base + y * row + x;
        const b = a + 1;
        const c = a + row;
        const d = c + 1;
        indices.push(a, c, b, b, c, d);
      }
    }
  }

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute("position", new THREE.Float32BufferAttribute(positions, 3));
  geometry.setAttribute("normal", new THREE.Float32BufferAttribute(normals, 3));
  geometry.setAttribute("color", new THREE.Float32BufferAttribute(colors, 3));
  geometry.setIndex(indices);
  geometry.computeBoundingSphere();

  const material = new THREE.MeshStandardMaterial({
    name: `planet-surface-${planet.id}`,
    vertexColors: true,
    roughness: 0.92,
    metalness: 0,
    flatShading: false,
  });
  const surface = new THREE.Mesh(geometry, material);
  surface.castShadow = false;
  surface.receiveShadow = false;
  surface.name = `planet:${planet.id}:surface`;

  const oceanRadius = radius + planet.seaLevelMeters + 2;
  const oceanMaterial = new THREE.MeshStandardMaterial({
    name: `planet-ocean-${planet.id}`,
    color: planet.palette.oceanShallow,
    transparent: true,
    opacity: 0.72,
    roughness: 0.18,
    metalness: 0.04,
    depthWrite: true,
    side: THREE.FrontSide,
  });
  const ocean = new THREE.Mesh(new THREE.SphereGeometry(oceanRadius, 64, 36), oceanMaterial);
  ocean.name = `planet:${planet.id}:ocean`;

  const atmosphereMaterial = new THREE.ShaderMaterial({
    name: `planet-atmosphere-${planet.id}`,
    transparent: true,
    depthWrite: false,
    side: THREE.BackSide,
    blending: THREE.AdditiveBlending,
    uniforms: {
      uColor: { value: new THREE.Color(planet.palette.atmosphere) },
      uOpacity: { value: 0.42 },
    },
    vertexShader: `
      varying vec3 vNormal;
      varying vec3 vView;
      void main() {
        vec4 worldPosition = modelMatrix * vec4(position, 1.0);
        vNormal = normalize(mat3(modelMatrix) * normal);
        vView = normalize(cameraPosition - worldPosition.xyz);
        gl_Position = projectionMatrix * viewMatrix * worldPosition;
      }
    `,
    fragmentShader: `
      uniform vec3 uColor;
      uniform float uOpacity;
      varying vec3 vNormal;
      varying vec3 vView;
      void main() {
        float rim = pow(1.0 - max(dot(vNormal, vView), 0.0), 2.7);
        gl_FragColor = vec4(uColor, rim * uOpacity);
      }
    `,
  });
  const atmosphere = new THREE.Mesh(new THREE.SphereGeometry(radius * 1.045, 64, 36), atmosphereMaterial);
  atmosphere.name = `planet:${planet.id}:atmosphere`;

  const group = new THREE.Group();
  group.name = `planet:${planet.id}`;
  group.add(surface, ocean, atmosphere);
  return { group, surface, ocean, atmosphere };
}
